<script setup lang="ts">
import { computed } from 'vue'
import { RouterView } from 'vue-router'
import { NConfigProvider, type GlobalThemeOverrides, darkTheme, esAR, dateEsAR } from 'naive-ui'
import { storeToRefs } from 'pinia'
import { useThemeStore } from '@/core/stores/theme.store'
import SessionExpiredModal from '@/shared/components/SessionExpiredModal.vue'

const { isDark } = storeToRefs(useThemeStore())

const naiveTheme = computed(() => (isDark.value ? darkTheme : null))

const darkAlertOverrides: GlobalThemeOverrides['Alert'] = {
  colorInfo: '#2d3645',
  borderInfo: '1px solid #424d66',
  titleTextColorInfo: '#d2dcff',
  contentTextColorInfo: '#d2dcff',
  iconColorInfo: '#8fa6ff',
  colorSuccess: '#1e2c22',
  borderSuccess: '1px solid #2f5c3f',
  titleTextColorSuccess: '#b6f0c8',
  contentTextColorSuccess: '#b6f0c8',
  iconColorSuccess: '#5fd99a',
  colorWarning: '#312614',
  borderWarning: '1px solid #6b5328',
  titleTextColorWarning: '#ffd9a8',
  contentTextColorWarning: '#ffd9a8',
  iconColorWarning: '#ffba3d',
  colorError: '#321f1f',
  borderError: '1px solid #6b3030',
  titleTextColorError: '#ffc4c4',
  contentTextColorError: '#ffc4c4',
  iconColorError: '#f87171',
}

const themeOverrides = computed<GlobalThemeOverrides>(() => ({
  common: {
    fontFamily: "'Manrope', 'Toteat Emoji', Helvetica, Arial, sans-serif",
    fontFamilyMono: "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Courier New', monospace",
    primaryColor: isDark.value ? '#ff6b61' : '#ff4236',
    primaryColorHover: isDark.value ? '#ff918a' : '#ff6b61',
    primaryColorPressed: isDark.value ? '#ff4236' : '#e63b30',
    primaryColorSuppl: '#ff918a',
  },
  ...(isDark.value ? { Alert: darkAlertOverrides } : {}),
}))
</script>

<template>
  <NConfigProvider
    :theme="naiveTheme"
    :theme-overrides="themeOverrides"
    :locale="esAR"
    :date-locale="dateEsAR"
  >
    <RouterView />
    <SessionExpiredModal />
  </NConfigProvider>
</template>
